/**
 * A monitor's morning "Fixed for you" digest (Platform Doctor): what it fixed by itself overnight.
 * Nothing to decide — it arrives in the Inbox as an update and opens here.
 */
import { useQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { ExternalLink, Wrench } from "lucide-react";
import { api } from "../lib/api";
import { Card, Empty, ErrorNote, PageHeader, fmtDate } from "../components/ui";

interface Digest {
  id: string; sourceName: string; title: string; link: string | null; more: number; createdAt: string;
  items: Array<{ kind?: string; title: string; detail?: string | null; host?: string | null; at?: string | null; link?: string | null }>;
}

export function DigestPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const q = useQuery({ queryKey: ["digest", id], queryFn: () => api<{ digest: Digest }>(`/api/digests/${id}`) });
  const d = q.data?.digest;
  return (
    <div className="space-y-4">
      <PageHeader eyebrow={d ? `${d.sourceName} · ${fmtDate(d.createdAt)}` : undefined} title={d?.title ?? "Digest"}
        actions={d?.link ? <a className="btn btn-sm" href={d.link} target="_blank" rel="noreferrer"><ExternalLink size={14} /> Open {d.sourceName}</a> : undefined} />
      <ErrorNote error={q.error} />
      {d && (
        <Card title="Fixed for you" icon={<Wrench size={16} />} count={d.items.length}>
          {d.items.length === 0 ? <Empty>Nothing needed fixing.</Empty> : (
            <ul className="divide-y divide-[var(--line)] text-sm">
              {d.items.map((i, n) => (
                <li key={n} className="py-2">
                  <div className="flex items-baseline gap-2">
                    <span className="font-medium">{i.title}</span>
                    {i.host && <span className="chip text-xs">{i.host}</span>}
                    {i.kind && <span className="muted text-xs">{i.kind}</span>}
                    {i.link && <a href={i.link} target="_blank" rel="noreferrer" className="ml-auto text-brand-700"><ExternalLink size={13} /></a>}
                  </div>
                  {i.detail && <p className="muted mt-0.5">{i.detail}</p>}
                </li>
              ))}
            </ul>
          )}
          {d.more > 0 && <p className="muted mt-2 text-xs">and {d.more} more{d.link ? ` — see ${d.sourceName}` : ""}.</p>}
        </Card>
      )}
    </div>
  );
}
